import { useMemo, useRef, useState, type FormEvent } from 'react'
import { htmlToPlainText } from '@/lib/messageFormat'
import {
  CheckSquare,
  ExternalLink,
  Eye,
  GitBranch,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Pin,
  Search,
  Settings,
  StickyNote,
  Trash2,
} from 'lucide-react'
import type { Activity } from '@/api/activities'
import { useMembers } from '@/features/team/useTeam'
import Select from '@/components/ui/Select'
import ConfirmButton from '@/components/ui/ConfirmButton'
import RichTextEditor, { type RichTextEditorHandle } from '@/components/RichTextEditor'
import RichText from '@/components/RichText'

const TYPES = ['note', 'call', 'email', 'sms', 'meeting'] as const

/** How much plain-text preview to show before "show more". */
const PREVIEW_CHARS = 220

/** Filter category → label; drives the chip row and the per-activity bucket. */
type Category = 'note' | 'email' | 'sms' | 'call' | 'task' | 'system'

const CATEGORY_LABEL: Record<Category, string> = {
  note: 'Notes',
  email: 'Email',
  sms: 'SMS',
  call: 'Calls',
  task: 'Tasks',
  system: 'System',
}

/** Colour classes for an activity badge, keyed by its category. */
const CATEGORY_STYLE: Record<Category, { badge: string; ring: string }> = {
  note: { badge: 'bg-amber-50 text-amber-600', ring: 'text-amber-500' },
  email: { badge: 'bg-blue-50 text-blue-600', ring: 'text-blue-500' },
  sms: { badge: 'bg-green-50 text-green-600', ring: 'text-green-500' },
  call: { badge: 'bg-teal-50 text-teal-600', ring: 'text-teal-500' },
  task: { badge: 'bg-emerald-50 text-emerald-600', ring: 'text-emerald-500' },
  system: { badge: 'bg-violet-50 text-violet-600', ring: 'text-violet-500' },
}

function categoryOf(activityType: string): Category {
  const t = activityType.toLowerCase()
  if (t === 'email') return 'email'
  if (t === 'sms') return 'sms'
  if (t === 'call' || t === 'meeting') return 'call'
  if (t === 'note') return 'note'
  if (t.startsWith('task')) return 'task'
  return 'system' // stage_change and anything else
}

/**
 * The icon element for an activity. A plain function (not a component) called as `{activityIcon(...)}`
 * so it isn't a component created during render (react-hooks/static-components).
 */
function activityIcon(activityType: string, className: string) {
  const props = { size: 13, className }
  if (activityType.toLowerCase() === 'stage_change') return <GitBranch {...props} />
  switch (categoryOf(activityType)) {
    case 'email':
      return <Mail {...props} />
    case 'sms':
      return <MessageSquare {...props} />
    case 'call':
      return <Phone {...props} />
    case 'note':
      return <StickyNote {...props} />
    case 'task':
      return <CheckSquare {...props} />
    default:
      return <Settings {...props} />
  }
}

/** Delivery-status pill styles — clearly separates sent / received / bounced / failed / drafted. */
const STATUS_STYLE: Record<string, string> = {
  sent: 'bg-blue-50 text-blue-600',
  received: 'bg-teal-50 text-teal-600',
  bounced: 'bg-red-50 text-red-600',
  failed: 'bg-red-50 text-red-600',
  drafted: 'bg-amber-50 text-amber-600',
}

/** A small delivery-status pill (plain function, not a component — called as `{statusBadge(...)}`). */
function statusBadge(status: string | null | undefined) {
  if (!status) return null
  const cls = STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-500'
  return (
    <span className={`shrink-0 text-[10px] uppercase tracking-wide rounded-full px-1.5 py-0.5 ${cls}`}>
      {status}
    </span>
  )
}

/**
 * Engagement pill for a sent email: what the recipient did with it. This is the buying signal an
 * agent reads — an opened appraisal email separates a lead weighing a decision from a silent one.
 * Nothing is shown until there is something to show; "0 opens" would just be noise on every send.
 */
function engagementBadge(engagement: Activity['engagement']) {
  if (!engagement || (engagement.opens === 0 && engagement.clicks === 0)) return null
  const { opens, clicks, lastOpenedAt, lastClickedAt } = engagement
  const parts: string[] = []
  if (opens > 0) parts.push(opens === 1 ? 'opened' : `opened ${opens}×`)
  if (clicks > 0) parts.push(clicks === 1 ? 'clicked' : `clicked ${clicks}×`)
  const last = lastClickedAt ?? lastOpenedAt
  return (
    <span
      title={last ? `Last engagement ${new Date(last).toLocaleString('en-AU')}` : undefined}
      className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700"
    >
      <Eye size={10} />
      {parts.join(' · ')}
    </span>
  )
}

/** Human label for the activity type ("stage_change" → "Stage change", "sms" → "SMS"). */
function labelFor(activityType: string): string {
  if (activityType.toLowerCase() === 'sms') return 'SMS'
  const spaced = activityType.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}


/**
 * Trim email signatures and unsubscribe/marketing footers from a plain-text preview so the timeline
 * stays scannable. Cuts at the standard "-- " signature delimiter and at the first line that reads
 * like an unsubscribe / preferences footer. Only affects the collapsed preview — the full body is
 * still shown on "show more".
 */
function stripBoilerplate(text: string): string {
  let out = text
  const sig = out.search(/\n--\s*\n/)
  if (sig !== -1) out = out.slice(0, sig)
  const footer = out.search(
    /\n[^\n]*(unsubscribe|opt[\s-]?out|to stop receiving|manage (your )?preferences|no longer wish to receive)/i,
  )
  if (footer !== -1) out = out.slice(0, footer)
  return out.trim()
}

/** Group key for an activity's date: "Today" / "Yesterday" / "9 Jul 2026". */
function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((start(today) - start(d)) / 86_400_000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Absolute date + time for the compact preview, e.g. "14 Aug 2026, 3:21 pm". */
function previewWhen(iso: string): string {
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date}, ${time}`
}

/** Minimal structural view of a react-query mutation — decouples the component from its hooks. */
interface Mutation<V> {
  mutate: (variables: V, options?: { onSuccess?: () => void }) => void
  isPending: boolean
}

/**
 * The activity timeline shared by the deal workspace and the contact card: log a note/call, then a
 * filterable, searchable, date-grouped feed of activity cards (icon + colour per type, author and
 * time, expandable long bodies with email signatures/unsubscribe footers hidden from the preview).
 * Manually logged entries can be edited/deleted; send-audit `system` rows stay read-only.
 */
export default function ActivityLog({
  activities,
  add,
  update,
  remove,
  pin,
  className,
  emptyLabel = 'No activity yet — log the first touch above.',
  limit,
  compact = false,
  hideComposer = false,
  onOpenTask,
}: {
  activities: Activity[] | undefined
  add: Mutation<{ activityType: string; body: string }>
  update: Mutation<{ activityId: string; body: string }>
  remove: Mutation<string>
  /** Pin/unpin an activity; omit to hide the pin control. */
  pin?: Mutation<{ activityId: string; pinned: boolean }>
  className?: string
  emptyLabel?: string
  /** Compact preview (e.g. the deal Overview): show only the newest {@code limit} entries. */
  limit?: number
  /** Compact, read-only preview — no composer/filters/day-groups — a minimal icon + snippet list. */
  compact?: boolean
  /** Hide the note composer: the log becomes pure history (notes are logged from their own card). */
  hideComposer?: boolean
  /**
   * Reveal the task a "Task created/completed" entry is about. Omit where there is no task list to
   * reveal it in (the contact card), and the entries stay plain text.
   */
  onOpenTask?: (taskId: string) => void
}) {
  const { data: members } = useMembers()
  const authorName = useMemo(
    () => new Map((members ?? []).map((m) => [m.id, m.fullName])),
    [members],
  )

  const [type, setType] = useState<string>('note')
  const [body, setBody] = useState('')
  const editorRef = useRef<RichTextEditorHandle>(null)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')

  const [filter, setFilter] = useState<Category | 'all'>('all')
  const [query, setQuery] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    add.mutate({ activityType: type, body }, { onSuccess: () => setBody('') })
  }

  function startEdit(a: Activity) {
    setEditingId(a.id)
    setEditBody(a.body)
  }

  function saveEdit(a: Activity) {
    if (!editBody.trim()) return
    update.mutate({ activityId: a.id, body: editBody }, { onSuccess: () => setEditingId(null) })
  }

  const all = useMemo(() => activities ?? [], [activities])

  // Which category chips to show — only those present, so the row isn't full of empty filters.
  const present = useMemo(() => {
    const set = new Set<Category>()
    all.forEach((a) => set.add(categoryOf(a.activityType)))
    return (Object.keys(CATEGORY_LABEL) as Category[]).filter((c) => set.has(c))
  }, [all])

  // Newest first, filtered by category + free-text search over the plain body.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return all
      .filter((a) => filter === 'all' || categoryOf(a.activityType) === filter)
      .filter((a) => !q || htmlToPlainText(a.body).toLowerCase().includes(q) || labelFor(a.activityType).toLowerCase().includes(q))
      .slice()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [all, filter, query])

  // Pinned entries float to the top in their own section; the rest group by day.
  const pinnedItems = useMemo(() => filtered.filter((a) => a.pinned), [filtered])
  const groups = useMemo(() => {
    const out: { label: string; items: Activity[] }[] = []
    for (const a of filtered) {
      if (a.pinned) continue
      const label = dayLabel(a.createdAt)
      const last = out[out.length - 1]
      if (last && last.label === label) last.items.push(a)
      else out.push({ label, items: [a] })
    }
    return out
  }, [filtered])

  function renderCard(a: Activity) {
    return (
      <ActivityCard
        key={a.id}
        activity={a}
        authorName={a.authorUserId ? authorName.get(a.authorUserId) ?? null : null}
        editing={editingId === a.id}
        editBody={editBody}
        onEditBodyChange={setEditBody}
        onStartEdit={() => startEdit(a)}
        onCancelEdit={() => setEditingId(null)}
        onSaveEdit={() => saveEdit(a)}
        savingEdit={update.isPending}
        onDelete={() => remove.mutate(a.id)}
        deleting={remove.isPending}
        onTogglePin={pin ? () => pin.mutate({ activityId: a.id, pinned: !a.pinned }) : undefined}
        pinning={pin?.isPending ?? false}
        onOpenTask={onOpenTask}
      />
    )
  }

  if (compact) {
    const shown = filtered.slice(0, limit ?? 5)
    return (
      <div className={className}>
        {all.length === 0 && <p className="text-xs text-gray-400">No activity yet.</p>}
        <div className="divide-y divide-gray-50">
          {shown.map((a) => {
            const style = CATEGORY_STYLE[categoryOf(a.activityType)]
            const author = a.authorUserId ? authorName.get(a.authorUserId) ?? null : null
            const snippet = stripBoilerplate(htmlToPlainText(a.body))
            return (
              <div key={a.id} className="flex items-start gap-2.5 py-2">
                <span className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${style.badge}`}>
                  {activityIcon(a.activityType, style.ring)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm text-brand-dark font-medium truncate">{labelFor(a.activityType)}</span>
                    {statusBadge(a.status)}
                    {engagementBadge(a.engagement)}
                    {a.taskId && onOpenTask && (
                      <button
                        type="button"
                        onClick={() => onOpenTask(a.taskId as string)}
                        title="Show this task"
                        className="shrink-0 text-[11px] text-brand-primary hover:underline"
                      >
                        Open task
                      </button>
                    )}
                  </div>
                  {snippet && <div className="text-xs text-gray-500 truncate">{snippet}</div>}
                </div>
                <div className="shrink-0 text-right text-[11px] text-gray-400 leading-tight">
                  <div>{previewWhen(a.createdAt)}</div>
                  <div>by {author ?? 'System'}</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {!hideComposer && <p className="text-sm font-semibold text-brand-darkest mb-2">Activity</p>}

      {!hideComposer && (
        <form onSubmit={submit} className="mb-3">
          <RichTextEditor
            ref={editorRef}
            value={body}
            onChange={setBody}
            placeholder="Log a note or a call…"
          />
          <div className="flex items-center gap-2 mt-1">
            <Select size="xs" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
            <button
              type="submit"
              disabled={add.isPending || !body.trim()}
              className="px-3 py-2 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
            >
              Add
            </button>
          </div>
        </form>
      )}

      {all.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 pt-3 border-t border-gray-100">
          <div className="flex flex-wrap gap-1">
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </FilterChip>
            {present.map((c) => (
              <FilterChip key={c} active={filter === c} onClick={() => setFilter(c)}>
                {CATEGORY_LABEL[c]}
              </FilterChip>
            ))}
          </div>
          <div className="relative ml-auto">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search activity…"
              className="w-40 pl-7 pr-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
          </div>
        </div>
      )}

      {all.length === 0 && <p className="text-xs text-gray-400">{emptyLabel}</p>}
      {all.length > 0 && filtered.length === 0 && (
        <p className="text-xs text-gray-400">No activity matches this filter.</p>
      )}

      <div className="space-y-4">
        {pinnedItems.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5 flex items-center gap-1">
              <Pin size={11} /> Pinned
            </p>
            <div className="space-y-1.5">{pinnedItems.map((a) => renderCard(a))}</div>
          </div>
        )}
        {groups.map((g) => (
          <div key={g.label}>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">{g.label}</p>
            <div className="space-y-1.5">{g.items.map((a) => renderCard(a))}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs rounded-full px-2.5 py-1 font-medium transition-colors ${
        active ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  )
}

function ActivityCard({
  activity: a,
  authorName,
  editing,
  editBody,
  onEditBodyChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  savingEdit,
  onDelete,
  deleting,
  onTogglePin,
  pinning,
  onOpenTask,
}: {
  activity: Activity
  authorName: string | null
  editing: boolean
  editBody: string
  onEditBodyChange: (v: string) => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  savingEdit: boolean
  onDelete: () => void
  deleting: boolean
  onTogglePin?: () => void
  pinning: boolean
  onOpenTask?: (taskId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const cat = categoryOf(a.activityType)
  const style = CATEGORY_STYLE[cat]

  // A system email/SMS body carries signatures + unsubscribe footers — strip them from the preview.
  const isSystemMessage = a.source === 'system' && (cat === 'email' || cat === 'sms')
  const plain = useMemo(() => stripBoilerplate(htmlToPlainText(a.body)), [a.body])
  const long = plain.length > PREVIEW_CHARS
  const time = new Date(a.createdAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })

  return (
    <div className="flex gap-2.5 rounded-lg border border-gray-100 bg-white p-2.5">
      <span className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${style.badge}`}>
        {activityIcon(a.activityType, style.ring)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium text-brand-dark">{labelFor(a.activityType)}</span>
          {statusBadge(a.status)}
          {engagementBadge(a.engagement)}
          {a.taskId && onOpenTask && (
            <button
              type="button"
              onClick={() => onOpenTask(a.taskId as string)}
              title="Show this task"
              className="inline-flex items-center gap-0.5 text-[11px] text-brand-primary hover:underline"
            >
              Open task <ExternalLink size={9} />
            </button>
          )}
          {a.source === 'system' && !a.status && !a.taskId && (
            <span className="text-[10px] uppercase text-gray-400">system</span>
          )}
          <span className="text-[11px] text-gray-400 ml-auto whitespace-nowrap">
            {authorName ? `${authorName} · ` : ''}
            {time}
          </span>
        </div>
        {/* A send goes out as the record's owner, which may not be who clicked Send — say so, and from which number. */}
        {(a.fromNumber || (a.senderName && a.senderName !== authorName)) && (
          <div className="text-[11px] text-gray-400">
            Sent as {a.senderName ?? authorName ?? 'the owner'}
            {a.fromNumber && <> · from {a.fromNumber}</>}
          </div>
        )}

        {editing ? (
          <div className="space-y-1 mt-1">
            <RichTextEditor value={editBody} onChange={onEditBodyChange} />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSaveEdit}
                disabled={savingEdit || !editBody.trim()}
                className="px-2.5 py-1 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
              >
                Save
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                className="px-2 py-1 text-xs text-gray-500 hover:text-brand-dark"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5 text-xs text-brand-dark">
            {expanded || (!long && !isSystemMessage) ? (
              <RichText html={a.body} />
            ) : (
              <p className="whitespace-pre-wrap break-words">
                {long ? `${plain.slice(0, PREVIEW_CHARS)}…` : plain}
              </p>
            )}
            {(long || isSystemMessage) && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[11px] text-brand-primary hover:underline mt-0.5"
              >
                {expanded ? 'show less' : 'show more'}
              </button>
            )}
          </div>
        )}
      </div>

      {!editing && (onTogglePin || a.source === 'manual') && (
        <div className="flex items-start gap-1 shrink-0">
          {onTogglePin && (
            <button
              type="button"
              title={a.pinned ? 'Unpin' : 'Pin to top'}
              onClick={onTogglePin}
              disabled={pinning}
              className={a.pinned ? 'text-brand-primary' : 'text-gray-400 hover:text-brand-primary'}
            >
              <Pin size={13} className={a.pinned ? 'fill-current' : ''} />
            </button>
          )}
          {a.source === 'manual' && (
            <>
              <button
                type="button"
                title="Edit"
                onClick={onStartEdit}
                className="text-gray-400 hover:text-brand-primary"
              >
                <Pencil size={13} />
              </button>
              <ConfirmButton title="Delete" onConfirm={onDelete} disabled={deleting}>
                <Trash2 size={13} />
              </ConfirmButton>
            </>
          )}
        </div>
      )}
    </div>
  )
}
